import type {
  StudyQueryFilters,
  GetDashboardInitialData,
} from 'lib/queries/dashboard';
import type { GetResultsAggregateCount } from 'lib/queries/studies';
import { ValueOf } from 'lib/types';

import React, { createContext, useContext, useMemo, useReducer } from 'react';
import { DASHBOARD_FILTERS } from 'lib/queries/dashboard';

export type DasboardContextData = {
  filters: StudyQueryFilters | null;
  initialData?: GetDashboardInitialData;
  loading: boolean;
  results: GetResultsAggregateCount;
  studies: GetDashboardInitialData['studies'];
};

export type DasboardContextAction =
  | {
      type: 'updateFilters';
      value: DasboardContextData['filters'];
    }
  | {
      type: 'updateData';
      key?: keyof DasboardContextData;
      value: Partial<DasboardContextData> | ValueOf<keyof DasboardContextData>;
    }
  | {
      type: 'toggleLoading';
      value: boolean;
    }
  | { type: 'reset' };

export type DasboardContextUpdate = React.Dispatch<DasboardContextAction>;

export type DasboardContextReturn = DasboardContextData & {
  dispatch: DasboardContextUpdate;
};

const DEFAULT_VALUES = {
  filters: null,
  loading: false,
  results: [],
  studies: { byYear: [], paginated: [], total: 0 },
};

export const DasboardContext = createContext<DasboardContextReturn>({
  ...DEFAULT_VALUES,
  dispatch: () => null,
});

function initState(state: Partial<DasboardContextReturn>): DasboardContextData {
  return {
    ...DEFAULT_VALUES,
    ...state,
  };
}

const dasboardContextReducer = (
  state: DasboardContextData,
  action: DasboardContextAction,
): DasboardContextData => {
  switch (action.type) {
    case 'reset': {
      return initState({
        initialData: state.initialData,
        results: state.initialData[DASHBOARD_FILTERS.RESULTS],
        studies: state.initialData.studies,
      });
    }
    case 'toggleLoading': {
      return {
        ...state,
        loading: action.value,
      };
    }
    case 'updateFilters': {
      return {
        ...state,
        filters: action.value,
        loading: action.value ? true : false,
      };
    }
    case 'updateData': {
      return {
        ...state,
        loading: false,
        ...(action?.key
          ? { [action.key]: action.value }
          : (action.value as Partial<GetDashboardInitialData>)),
      };
    }
  }
};

export function useDasboardContext(): DasboardContextReturn {
  return useContext(DasboardContext);
}

export default function DasboardProvider({
  children,
  initialValues,
}: {
  children: React.ReactElement | React.ReactElement[];
  initialValues: GetDashboardInitialData;
}): JSX.Element {
  const [state, dispatch] = useReducer(
    dasboardContextReducer,
    {
      initialData: initialValues,
      results: initialValues[DASHBOARD_FILTERS.RESULTS],
      studies: initialValues.studies,
    },
    initState,
  );

  const contextValue = useMemo(
    () => ({ ...state, dispatch }),
    [state, dispatch],
  );

  return (
    <DasboardContext.Provider value={contextValue}>
      {children}
    </DasboardContext.Provider>
  );
}
